import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button, Card } from '@clari/ui'
import { ErrorBanner } from '@/components/ErrorBanner'
import { KycSdkEmbed } from '@/components/KycSdkEmbed'
import { apiErrorMessage } from '@/lib/api-error'
import { kycApi } from '@/lib/auth-api'
import { useAuthStore } from '@/stores/authStore'

export function Kyc() {
  const { t, i18n } = useTranslation('auth')
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const [token, setToken] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  async function start() {
    setError(null)
    setLoading(true)
    try {
      const res = await kycApi.start()
      setToken(res.access_token)
    } catch (err) {
      setError(apiErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  if (token) {
    return (
      <div className="mx-auto max-w-2xl">
        <KycSdkEmbed
          accessToken={token}
          lang={i18n.resolvedLanguage ?? i18n.language}
          onComplete={() => navigate('/app/onboarding/kyc/pending', { replace: true })}
          onError={() => setError(t('kyc.error'))}
        />
        {error ? (
          <div className="mt-4">
            <ErrorBanner message={error} />
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-xl">
      <Card>
        <h1 className="text-2xl font-semibold">{t('kyc.start_title')}</h1>
        <p className="mt-2 text-text-muted">{t('kyc.start_body')}</p>
        <p className="mt-2 text-sm text-text-subtle">
          {user?.plan === 'free' ? t('kyc.tier_light_note') : t('kyc.tier_full_note')}
        </p>
        {error ? (
          <div className="mt-4">
            <ErrorBanner message={error} />
          </div>
        ) : null}
        <Button className="mt-6" onClick={start} disabled={loading}>
          {t('kyc.start_cta')}
        </Button>
      </Card>
    </div>
  )
}
